import { ref, computed } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAcademySelection from '@/composables/useAcademySelection'
import { getAcademyDisplay, getContentPackItemIds } from '@/utils/contentPackAcademyDisplay'

export default function useGolfAssessments() {
  const RESOURCE = '/assessment/golf'
  const { showSnackbar } = useSnackbar()
  const { getContentScopeParams } = useAcademySelection()

  const baseHeaders = [
    { text: 'UNIQUE KEY', value: 'uniqueKey' },
    { text: 'TITLE OF SESSION', value: 'titleOfSession' },
    { text: 'SESSION TYPE', value: 'sessionType' },
    { text: 'CATEGORY', value: 'category' },
    { text: 'LEVEL', value: 'level' },
    { text: 'FREQUENCY', value: 'frequency' },
    { text: 'MEASUREMENT', value: 'measurement' },
    { text: 'PRACTICE TYPE', value: 'practiceType' },
    { text: 'GOAL', value: 'goal' },
    { text: 'EXERCISE', value: 'exercise' },
    { text: 'EDM', value: 'entryDataMethod' },
    { text: 'ATTEMPTS', value: 'attempts' },
    { text: 'DISTANCE', value: 'distance' },
    { text: 'DISTANCE UNIT', value: 'distanceUnit' },
    {
      text: 'CREATOR', value: 'creator', sortable: true, width: 160,
    },
    {
      text: 'ACADEMY', value: 'academy', sortable: true, width: 160,
    },
    {
      text: 'ACTIVE', value: 'active', width: 100, class: 'compact-table-column', cellClass: 'compact-table-column',
    },
    {
      text: 'ACTIONS', value: 'actions', sortable: false, width: 100, align: 'center', class: 'compact-table-column', cellClass: 'compact-table-column',
    },
  ]

  const dtHeaders = computed(() => [...baseHeaders])
  const dtSearchQuery = ref('')
  const dtExpanded = ref([])

  const blankGolfAssessment = {
    id: '',
    uniqueKey: '',
    sessionType: '',
    titleOfSession: '',
    category: '',
    level: '',
    frequency: '',
    measurement: '',
    practiceType: '',
    goal: '',
    entryDataMethod: '',
    attempts: '',
    distance: '',
    distanceUnit: '',
    exercise: null,
    exerciseUniqueKey: '',
  }

  const golfAssessments = ref([])
  const golfAssessment = ref(structuredClone(blankGolfAssessment))
  const isLoading = ref(false)

  const fetchGolfAssessments = async () => {
    isLoading.value = true
    const params = { withCreator: true, ...getContentScopeParams() }

    try {
      const [response, contentPacksResponse] = await Promise.all([
        axios.get(RESOURCE, { params }),
        axios.get('/content-pack').catch(() => ({ data: [] })),
      ])
      const contentPackAssessmentIds = ['fitness', 'practice', 'flow'].reduce((result, sectionKey) => ({
        ...result,
        [sectionKey]: getContentPackItemIds(contentPacksResponse.data, sectionKey, 'assessments'),
      }), {})
      golfAssessments.value = response.data.map(assessment => ({
        ...assessment,
        academy: getAcademyDisplay(
          assessment,
          contentPackAssessmentIds[String(assessment.category || '').toLowerCase()] || new Set(),
        ),
      }))
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to fetch: ${message}`, SnackbarType.ERROR)
    } finally {
      isLoading.value = false
    }
  }

  const createGolfAssessment = async golfAssessmentItem => {
    isLoading.value = true
    try {
      await axios.post(RESOURCE, golfAssessmentItem)
      showSnackbar('You have successfully created a new golf assessment', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to create: ${message}`, SnackbarType.ERROR)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const deleteGolfAssessment = async golfAssessmentItem => {
    isLoading.value = true
    golfAssessmentItem.deleted = true
    try {
      await axios.put(RESOURCE, golfAssessmentItem)
      showSnackbar('You have successfully deleted the golf assessment', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to delete: ${message}`, SnackbarType.ERROR)
    } finally {
      isLoading.value = false
    }
  }

  const updateGolfAssessment = async golfAssessmentItem => {
    isLoading.value = true
    try {
      await axios.put(RESOURCE, golfAssessmentItem)
      showSnackbar('You have successfully updated the golf assessment', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to update: ${message}`, SnackbarType.ERROR)
    } finally {
      isLoading.value = false
    }
  }

  const updateManyGolfAssessments = async (type, paramName, paramValue, ids) => {
    isLoading.value = true
    const payload = {
      type,
      params: [{ name: paramName, value: paramValue, ids }],
    }
    try {
      await axios.put('/library/updateMany', payload)
      showSnackbar(`Successfully updated ${ids.length} items`, SnackbarType.SUCCESS)

      return true
    } catch (error) {
      showSnackbar('Failed to update items', SnackbarType.ERROR)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetState = () => {
    golfAssessments.value = []
    golfAssessment.value = structuredClone(blankGolfAssessment)
    dtSearchQuery.value = ''
    dtExpanded.value = []
    isLoading.value = false
  }

  return {
    dtHeaders,
    dtSearchQuery,
    dtExpanded,
    blankGolfAssessment,
    golfAssessments,
    golfAssessment,
    isLoading,
    createGolfAssessment,
    deleteGolfAssessment,
    fetchGolfAssessments,
    updateGolfAssessment,
    updateManyGolfAssessments,
    resetState,
  }
}
